import { MAX_64_CHARACTERS, MAX_65535_CHARACTERS } from "@/typings/formik"
import { BoxOfferDraftEntity, type BoxOfferDraftState } from "@modules/boxOffer/entities/boxOfferEntity"
import {
  BOX_OFFER_TICKET_BOOKS_MAX,
  BOX_OFFER_TICKET_BOOKS_MIN,
  BOX_OFFER_VALIDITY_MAX,
  BOX_OFFER_VALIDITY_MIN,
  BOX_OFFER_WEEKLY_PARTICIPATION_MAX,
  BOX_OFFER_WEEKLY_PARTICIPATION_MIN,
  BoxOfferPriceType,
  BoxOfferRulePeriod,
  BoxOfferRuleSchema,
  BoxOfferType,
  BoxOfferValidity,
  BoxOfferWeeklyParticipationType,
  OfferAutonomousPurchase,
  type BoxOfferRule,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { z } from "zod"

export type BoxOfferFormValues = BoxOfferDraftState

export const boxOfferFormDefaultValues: BoxOfferFormValues = BoxOfferDraftEntity.toDraft()

const boxOfferFormBaseSchema = z.object({
  type: z.enum(BoxOfferType),
  name: z.string(),
  description: z.string(),
  priceSwitch: z.enum(BoxOfferPriceType),
  price: z.number().nullable(),
  currency: z.string().optional(),
  weeklyParticipationSwitch: z.enum(BoxOfferWeeklyParticipationType),
  maxWeeklyParticipation: z.number().optional(),
  minimumMonthlyDuration: z.number().optional(),
  commitmentPeriodAutoRenewal: z.boolean().optional(),
  recurringPeriod: z.string().optional(),
  programs: z.array(z.string()),
  rules: z.array(BoxOfferRuleSchema).optional(),
  renewable: z.boolean().optional(),
  validityPeriodMonths: z.number().optional(),
  validitySwitch: z.enum(BoxOfferValidity),
  showcaseDisplay: z.boolean(),
  priceDisplay: z.boolean(),
  autonomousPurchase: z.enum(OfferAutonomousPurchase),
  archived: z.boolean().optional(),
  count: z.number().optional(),
})

export function getBoxOfferValidationSchema(
  language: string,
  pageNumber: number,
  stripeMinimumAmount: number,
  stripeMaximumAmount: number,
  stripeAccountCurrency?: string,
): z.ZodType<BoxOfferFormValues, BoxOfferFormValues> {
  switch (pageNumber) {
    case 1:
      return firstPageSchema(language, stripeMinimumAmount, stripeMaximumAmount, stripeAccountCurrency)
    case 2:
      return secondPageSchema()
    case 3:
      return thirdPageSchema()
    default:
      return boxOfferFormBaseSchema
  }
}

const firstPageSchema = (
  language: string,
  stripeMinimumAmount: number,
  stripeMaximumAmount: number,
  stripeAccountCurrency?: string,
) => {
  const countErrorMessage = word("offer.form.count.error", {
    min: BOX_OFFER_TICKET_BOOKS_MIN,
    max: BOX_OFFER_TICKET_BOOKS_MAX,
  })

  return boxOfferFormBaseSchema
    .extend({
      name: z
        .string()
        .min(1, word("global.form.required"))
        .max(MAX_64_CHARACTERS, word("form.maximum.characters", { count: MAX_64_CHARACTERS })),
      description: z
        .string()
        .min(1, word("global.form.required"))
        .max(MAX_65535_CHARACTERS, word("form.maximum.characters", { count: MAX_65535_CHARACTERS })),
    })
    .superRefine((values, ctx) => {
      const isPriceValid =
        (!!values.price &&
          stripeMinimumAmount > 0 &&
          values.price >= stripeMinimumAmount &&
          values.price <= stripeMaximumAmount) ||
        values.priceSwitch === BoxOfferPriceType.Free
      if (!isPriceValid) {
        ctx.addIssue({
          code: "custom",
          path: ["price"],
          message: word("box.offer.error.price", {
            minPrice: formatCurrency(stripeMinimumAmount, stripeAccountCurrency, language),
            maxPrice: formatCurrency(stripeMaximumAmount, stripeAccountCurrency, language),
          }),
        })
      }

      if (!values.currency) {
        ctx.addIssue({ code: "custom", path: ["currency"], message: word("global.form.required") })
      }

      if (values.type === BoxOfferType.TicketsBook) {
        if (values.count == null || Number.isNaN(values.count)) {
          ctx.addIssue({ code: "custom", path: ["count"], message: word("global.form.required") })
        } else if (values.count < BOX_OFFER_TICKET_BOOKS_MIN || values.count > BOX_OFFER_TICKET_BOOKS_MAX) {
          ctx.addIssue({ code: "custom", path: ["count"], message: countErrorMessage })
        }
      }

      if (values.type === BoxOfferType.Subscription) {
        if (values.minimumMonthlyDuration == null) {
          ctx.addIssue({ code: "custom", path: ["minimumMonthlyDuration"], message: word("global.form.required") })
        }
        if (values.commitmentPeriodAutoRenewal == null) {
          ctx.addIssue({
            code: "custom",
            path: ["commitmentPeriodAutoRenewal"],
            message: word("global.form.required"),
          })
        }
        if (!values.recurringPeriod) {
          ctx.addIssue({ code: "custom", path: ["recurringPeriod"], message: word("global.form.required") })
        }
      }

      if (
        !values.maxWeeklyParticipation &&
        values.weeklyParticipationSwitch !== BoxOfferWeeklyParticipationType.Unlimited
      ) {
        ctx.addIssue({ code: "custom", path: ["maxWeeklyParticipation"], message: word("global.form.required") })
      } else if (
        values.maxWeeklyParticipation != null &&
        (values.maxWeeklyParticipation < BOX_OFFER_WEEKLY_PARTICIPATION_MIN ||
          values.maxWeeklyParticipation > BOX_OFFER_WEEKLY_PARTICIPATION_MAX)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["maxWeeklyParticipation"],
          message:
            values.maxWeeklyParticipation < BOX_OFFER_WEEKLY_PARTICIPATION_MIN
              ? word("global.form.minimumValue", { minValue: BOX_OFFER_WEEKLY_PARTICIPATION_MIN })
              : word("global.form.maximumValue", { maxValue: BOX_OFFER_WEEKLY_PARTICIPATION_MAX }),
        })
      }

      if (values.validitySwitch !== BoxOfferValidity.UNLIMITED) {
        if (!values.validityPeriodMonths || values.validityPeriodMonths < BOX_OFFER_VALIDITY_MIN) {
          ctx.addIssue({
            code: "custom",
            path: ["validityPeriodMonths"],
            message: word("global.form.minimumValue", { minValue: BOX_OFFER_VALIDITY_MIN }),
          })
        } else if (values.validityPeriodMonths > BOX_OFFER_VALIDITY_MAX) {
          ctx.addIssue({
            code: "custom",
            path: ["validityPeriodMonths"],
            message: word("global.form.maximumValue", { maxValue: BOX_OFFER_VALIDITY_MAX }),
          })
        }
      }
    })
}

const secondPageSchema = () => {
  return boxOfferFormBaseSchema
    .extend({
      programs: z.array(z.string()).min(1, word("box.offer.error.programs")),
    })
    .superRefine((values, ctx) => {
      if (values.type === BoxOfferType.Trial) {
        if (values.count == null || Number.isNaN(values.count)) {
          ctx.addIssue({ code: "custom", path: ["count"], message: word("global.form.required") })
        }
        if (values.renewable == null) {
          ctx.addIssue({ code: "custom", path: ["renewable"], message: word("global.form.required") })
        }
      }
    })
}

const thirdPageSchema = () => {
  return boxOfferFormBaseSchema
}

export const boxOfferRuleValidationSchema = (rules: BoxOfferRule[]) =>
  z
    .object({
      value: z.number().min(1, word("global.form.minimumValue", { minValue: 1 })),
      period: z.enum(BoxOfferRulePeriod),
    })
    .refine(
      ({ value, period }) => {
        const existingRule = rules.find((rule) => rule.period === period)
        if (existingRule?.value === value) return true

        const dayRule = rules.find((rule) => rule.period === BoxOfferRulePeriod.DAY)
        const weekRule = rules.find((rule) => rule.period === BoxOfferRulePeriod.WEEK)
        const monthRule = rules.find((rule) => rule.period === BoxOfferRulePeriod.MONTH)
        switch (period) {
          case BoxOfferRulePeriod.DAY:
            return weekRule?.value
              ? value !== existingRule?.value && value <= weekRule.value
              : value !== existingRule?.value && (!monthRule || value <= monthRule.value / 30)
          case BoxOfferRulePeriod.WEEK:
            return (
              value !== existingRule?.value &&
              (!dayRule || value <= dayRule.value * 7) &&
              (!monthRule || value <= monthRule.value / 4)
            )
          case BoxOfferRulePeriod.MONTH:
            return (
              value !== existingRule?.value &&
              (!dayRule || value <= dayRule.value * 30) &&
              (!weekRule || value <= weekRule.value * 4)
            )
          default:
            return false
        }
      },
      { error: word("box.offer.form.infos.restrictedPeriod.error"), path: ["value"] },
    )
